import {
  computed,
  inject,
  markRaw,
  provide,
  ref,
  watch,
} from 'vue'
import { useRouteQuery } from '@vueuse/router'
import dev from '@nicolaudie-group/ng-ui-kit/helpers/dev'

import ALL_FILTERS, {
  FILTER_MINE,
  FILTER_ALL,
  FILTER_CERTIFIED,
  FILTER_TRASH,
} from '@/helpers/homeFilters'
import useStatus from '@nicolaudie-group/ng-ui-kit/composables/useStatus'
import useAction from '@/composables/useAction'
import * as sdk from '@/helpers/sdk'
import buildDownloadLink from '@/helpers/profile/buildDownloadLink'
import formatDate from '@/helpers/formatDate'
import routeNames from '@/router/names'
import PbLink from '@/components/PbLink.vue'
import { useRoute } from 'vue-router'
import useRef from '@nicolaudie-group/ng-ui-kit/composables/useRef'
import PbError from '@/classes/PbError.class'
import useSortParams from './DataView/useSortParams'
import { checkChanged, getRoute } from './DataView/helpers'
import usePaginationParams from './DataView/usePaginationParams'
import useAutofix from './DataView/useAutofix'
// import useRefSync from '@nicolaudie-group/ng-ui-kit/composables/useRefSync'
// import eagerComputed from '@nicolaudie-group/ng-ui-kit/helpers/eagerComputed'

export const SORTBY_NAME = 'name'
export const SORTBY_BRAND = 'brand'
export const SORTBY_UPDATED = 'updatedDate'

export const ALL_SORTBY = [SORTBY_NAME, SORTBY_BRAND, SORTBY_UPDATED]
export const DEFAULT_SORTBY = SORTBY_UPDATED

export const DEFAULT_FILTER = FILTER_MINE

export const DEFAULT_NAME = ''
export const DEFAULT_BRAND = ''

export {
  ALL_FILTERS,
  FILTER_MINE,
  FILTER_CERTIFIED,
  FILTER_TRASH,
  FILTER_ALL,
}

const PROFILE_LIST_PROVIDE_KEY = Symbol('Profile list')

/**
 * Make sure we always get a valid filter from the enum, instead of strings or undefined
 */
function fixFilterParam(value) {
  // checkLimit('Fix filter', value)
  if (ALL_FILTERS.includes(value)) {
    return value
  }
  // Fallback to the default filter if unrecognized
  return DEFAULT_FILTER
}

/**
 * Make sure we always get a string, instead of undefined
 */
function parseStringParam(value) {
  // checkLimit('Parse string', value)
  return value || ''
}

/**
 * Transform empty string to undefined, to remove it from query
 */
function serializeStringParam(value) {
  // checkLimit('Serialize string', value)
  return value || undefined
}

export function useProfileQuery() {
  const { action: get, data: response, status } = useAction(
    async ({ page, limitBy, ...query }) => {
      const result = await sdk.v3.profiles.getMany({
        query: {
          nPage: page,
          nLimit: limitBy,
          ...query,
        },
      })
      return markRaw(result)
    },
    {
      /*
      We need the full SDK Result object for pagination, but Vue can't make it
      reactive (see this issue https://github.com/vuejs/core/issues/3024).
      As a workaround, we use a shallowRef so that it's not making it deeply reactive.
      Which is better anyways, since we (should) never mutate the result anyways,
      saving us a little bit of resources here.
      */
      shallow: true,
    },
  )

  const profiles = computed(() => (
    (response.value ? response.value.data : []).map((item) => ({
      ...item,
      formattedUpdatedDate: formatDate(item.updatedDate),
      link: {
        name: routeNames.Profile,
        params: {
          profileUid: item.uid,
        },
      },
      downloadLink: buildDownloadLink(item.uid, 'ssl2'),
    }))
  ))

  const count = computed(() => (
    response.value ? response.value.pagination.count : 0
  ))

  return {
    get,
    profiles,
    count,
    status,
  }
}

// function useBidirectional() {
//   const bidirectional = ref(true)
//   /*
//   When navigating away to a route that doesn't use the queries, this will trigger one last
//   change the various values synced to queries (they will change to undefined).

//   That change will cause the various watchers on those values (ie. page or limit autofix) to
//   change it back from undefined to some fallback, and update the queries again in the URL with
//   those fallbacks.

//   Since we don't want those queries we navigated away from back in the URL, we stop syncing from
//   ref to query when there is a route change that will unmount the component (onBeforeRouteLeave)

//   See:
//   https://router.vuejs.org/guide/advanced/navigation-guards.html#Using-the-options-API
//   https://router.vuejs.org/guide/advanced/navigation-guards.html#The-Full-Navigation-Resolution-Flow
//   */
//   onBeforeRouteLeave(() => {
//     bidirectional.value = false
//   })

//   return bidirectional
// }

function useProfileListFilter({
  query = {},
  loading = false,
} = {}) {
  const {
    enabled: queryEnabled = false,
    mode: queryMode,
  } = query

  const loadingRef = useRef(loading)

  function getFilterRoute(route, newFilter) {
    return getRoute(route, { filter: newFilter, page: undefined })
  }

  const filter = queryEnabled
    ? useRouteQuery('filter', DEFAULT_FILTER, {
      mode: queryMode,
      transform: { get: fixFilterParam, set: fixFilterParam },
    })
    : ref(DEFAULT_FILTER)

  if (!queryEnabled) {
    useAutofix(filter, fixFilterParam)
  }

  /**
   * Filter flag for trash
   */
  const trashFilterEnabled = computed(() => filter.value === FILTER_TRASH)
  /**
   * Filter flag for certified profiles
   */
  const officialFilterEnabled = computed(() => filter.value === FILTER_CERTIFIED)
  /**
   * Filter flag for own profiles
   */
  const mineFilterEnabled = computed(() => filter.value === FILTER_MINE)
  /**
   * Filter flag for all profiles
   */
  const allFilterEnabled = computed(() => filter.value === FILTER_ALL)

  /**
   * Computed owner parameter from the filter
   */
  const owner = computed(() => {
    if (mineFilterEnabled.value) {
      return 'mine'
    }
    if (allFilterEnabled.value) {
      return 'all'
    }
    return undefined
  })

  const route = useRoute()

  function getCommonProp() {
    return {
      disabledClass: 'base-button-disabled',
      disabled: loadingRef.value,
    }
  }

  function getQueryProp(filterName) {
    const defaultActive = filterName === DEFAULT_FILTER ? {
      active: filter.value === filterName,
    } : {}
    return {
      ...getCommonProp(),
      as: PbLink,
      matchQuery: ['filter'],
      to: getFilterRoute(route, filterName),
      replace: queryMode === 'replace',
      ...defaultActive,
    }
  }

  function getRegularProp(filterName) {
    return {
      active: filter.value === filterName,
      onClick: () => { filter.value = filterName },
    }
  }

  const filterProps = computed(() => {
    if (queryEnabled) {
      return {
        official: getQueryProp(FILTER_CERTIFIED),
        mine: getQueryProp(FILTER_MINE),
        all: getQueryProp(FILTER_ALL),
        trash: getQueryProp(FILTER_TRASH),
      }
    }
    return {
      official: getRegularProp(FILTER_CERTIFIED),
      mine: getRegularProp(FILTER_MINE),
      all: getRegularProp(FILTER_ALL),
      trash: getRegularProp(FILTER_TRASH),
    }
  })

  return {
    loading: loadingRef,
    filter,
    trashFilterEnabled,
    officialFilterEnabled,
    mineFilterEnabled,
    allFilterEnabled,
    owner,
    getFilterRoute,
    filterProps,
  }
}

export function useProfileListSearch({
  query = {},
  loading = false,
} = {}) {
  const {
    enabled: queryEnabled = false,
    mode: queryMode,
  } = query

  const loadingRef = useRef(loading)

  function getSearchRoute(route, { brand, name } = {}) {
    return getRoute(route, {
      brand: brand || undefined,
      name: name || undefined,
      page: undefined,
    })
  }

  const name = queryEnabled
    ? useRouteQuery('name', DEFAULT_NAME, {
      mode: queryMode,
      transform: { get: parseStringParam, set: serializeStringParam },
    })
    : ref(DEFAULT_NAME)

  const brand = queryEnabled
    ? useRouteQuery('brand', DEFAULT_BRAND, {
      mode: queryMode,
      transform: { get: parseStringParam, set: serializeStringParam },
    })
    : ref(DEFAULT_BRAND)

  /**
   * Ref to temporary hold the name filter input by the user, before it gets committed to
   * the query filters when the user confirms the search.
   *
   * We only use one-way sync from query, because we just want the input to get the value
   * provided in queries, but don't want the query to update until search is confirmed.
   */
  const tempName = ref(DEFAULT_NAME)
  const tempBrand = ref(DEFAULT_BRAND)
  watch(name, (newValue, prevValue) => {
    if (newValue !== prevValue) {
      tempName.value = newValue
    }
  }, { immediate: true })
  watch(brand, (newValue, prevValue) => {
    if (newValue !== prevValue) {
      tempBrand.value = newValue
    }
  }, { immediate: true })

  function applySearch() {
    name.value = tempName.value
    brand.value = tempBrand.value
  }

  /**
   * True when one of the search fields is filled and used to search
   */
  const hasSearched = computed(() => {
    const hasName = !!name.value
    const hasBrand = !!brand.value
    return hasName || hasBrand
  })

  /**
   * To enable the search submit button when the name/brand has changed from previous search
   */
  const searchEnabled = computed(() => {
    const nameChanged = name.value !== tempName.value
    const brandChanged = brand.value !== tempBrand.value

    /*
    Enable the "search" button if a name or brand was entered, and
    if it's different from the current search
    */
    return nameChanged || brandChanged
  })

  const route = useRoute()

  const searchProps = computed(() => {
    let submit = {}

    if (searchEnabled.value || !hasSearched.value) {
      submit = {
        icon: 'search',
        class: 'pb-profile-list-search-btn btn-success',
        disabled: !searchEnabled.value || loadingRef.value,
      }

      if (queryEnabled) {
        submit.to = getSearchRoute(route, {
          name: tempName.value,
          brand: tempBrand.value,
        })
      } else {
        submit.onClick = applySearch
      }
    } else {
      submit = {
        icon: 'remove',
        class: 'pb-profile-list-search-btn btn-warning',
        disabled: loadingRef.value,
      }

      if (queryEnabled) {
        submit.to = getSearchRoute(route)
      } else {
        submit.onClick = () => {
          tempName.value = DEFAULT_NAME
          tempBrand.value = DEFAULT_BRAND
          applySearch()
        }
      }
    }

    return {
      // name,
      // brand,
      submit,
    }
  })

  return {
    loading: loadingRef,
    tempName,
    tempBrand,
    name,
    brand,
    hasSearched,
    searchEnabled,
    applySearch,
    getSearchRoute,
    searchProps,
  }
}

function parseQueryArgument(queryArgument) {
  return {
    enabled: !!queryArgument,
    mode: typeof queryArgument === 'string' ? queryArgument : undefined,
  }
}

function useProfileList({
  filterQuery,
  paginationQuery,
  sortQuery,
  searchQuery,
} = {}) {
  const queries = {
    filter: parseQueryArgument(filterQuery),
    search: parseQueryArgument(searchQuery),
    pagination: parseQueryArgument(paginationQuery),
    sort: parseQueryArgument(sortQuery),
  }

  const {
    count,
    get,
    profiles,
    status,
  } = useProfileQuery()

  const { pending: loading, success: loaded } = useStatus(status)

  const filter = useProfileListFilter({ query: queries.filter, loading })

  const pagination = usePaginationParams(count, loaded, { query: queries.pagination, loading })

  const sort = useSortParams({
    query: queries.sort, loading, ALL_SORTBY, DEFAULT_SORTBY,
  })

  const search = useProfileListSearch({ query: queries.search, loading })

  /*
  Using "previous" provided to computedGetter to detect changes (not yet documented):
  https://github.com/vuejs/core/pull/9497
  */
  const requestParams = computed((previous = {}) => {
    const result = {
      ...previous,
      page: pagination.page.value,
      limitBy: pagination.limit.value,
      sortBy: sort.sortBy.value,
      sortOrder: sort.sortOrder.value,
      name: search.name.value || undefined,
      brand: search.brand.value || undefined,
      isCertificated: filter.officialFilterEnabled.value || undefined,
      ownerUid: filter.owner.value,
      isRemove: filter.trashFilterEnabled.value || undefined,
    }

    // We check for changes to break the effects chain if no filter changed
    return checkChanged(result, previous)
  })

  async function load() {
    dev.info('Loading profiles', requestParams.value)
    await get(requestParams.value)
  }

  const context = {
    filter,
    pagination,
    sort,
    search,
    requestParams,
    load,
    loading,
    loaded,
    profiles,
    queries,
  }

  provide(PROFILE_LIST_PROVIDE_KEY, context)

  return context
}

/**
 * @returns {ReturnType<typeof useProfileList> | undefined}
 */
export function useProfileListInject() {
  const injected = inject(PROFILE_LIST_PROVIDE_KEY)
  if (!injected) {
    throw new PbError({
      title: 'Missing profile list context',
      message: 'Please call "useProfileList" in the parent component to provide context to the ProfileList component',
    })
  }
  return injected
}

export default useProfileList

/**
 * TODO:
 * [~] Check with query disabled
 * [x] Decouple from profileStore
 * [ ] Integrate in requestView
 */
